import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CompanyView } from "./companyView";
import { LoadingIndicator } from "../../shared/alerts/loadingIndicator";
import { useGetCompanyByIdQuery, useDeleteCompanyWithDependenciesMutation } from "../../../api/companyApi";
import { useGetComapanyOwnersQuery } from "../../../api/userApi";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { ConfirmationDialog } from "../../shared/confirmationDialog/confirmationDialog";
import { useToast } from "../../../hooks/useToast";
import { useLoader } from "../../../hooks/useLoader";
import { useDispatch } from "react-redux";
import { impersonateUser } from "../../../api/authApi";
import { AppDispatch } from "../../../api/store";

export const CompanyViewContainer: React.FC = () => {
    const { companyId } = useParams<{ companyId?: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { data, isLoading, error } = useGetCompanyByIdQuery(companyId || "");
    const { data: owners } = useGetComapanyOwnersQuery({ companyId: companyId || "" });
    const [deleteCompanyWithDependencies] = useDeleteCompanyWithDependenciesMutation();
    const { isAdmin, user } = useAuthenticatedUser();
    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    // Confirmation dialog state
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    // Handle loading and error states
    if (isLoading) return <LoadingIndicator message="Loading company data..." />;
    if (error) return <div>Error fetching company data.</div>;
    if (!data) {
        return <p>Company not found</p>;
    };

    const handleDeleteClick = () => {
        setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (companyId) {
            try {
                showLoader("Deleting company...");
                await deleteCompanyWithDependencies(companyId).unwrap();
                hideLoader();
                showSuccess("Company deleted successfully");
                navigate("/admin");
            } catch (err: any) {
                hideLoader();
                if (err?.status === 400) {
                    showError(err?.message || "Cannot delete company.");
                } else {
                    showError("Failed to delete company");
                }
            }
        }
        setConfirmDialogOpen(false);
    };

    const handleCancelDelete = () => {
        setConfirmDialogOpen(false);
    };

    const handleLoginAsOwner = () => {
        try {
            const owner = owners?.[0];
            if (!owner) {
                showError("Add a company owner to enable login as owner functionality");
                return;
            }
            if (!user) {
                showError("Current admin user not found");
                return;
            }

            dispatch(impersonateUser({ adminUser: user, targetUser: owner }));
            showSuccess(`Now logged in as ${owner.firstName} ${owner.lastName}`);
            navigate('/');
        } catch (error: any) {
            showError(`Failed to login as owner: ${error.message || "Unknown error"}`);
        }
    };

    return (
        <>
            <CompanyView
                company={data}
                onDelete={isAdmin ? handleDeleteClick : undefined}
                onLoginAsOwner={isAdmin ? handleLoginAsOwner : undefined}
            />
            <ConfirmationDialog
                open={confirmDialogOpen}
                title="Delete Company"
                message="Are you sure you want to delete this company? This will delete all users and associated data. This action cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </>
    );
};

